import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isoWeek);

/**
 * Common date formats for hint-based parsing. 
 * Note: We now prioritize native Day.js parsing but use these as hints for ambiguous cases.
 */
export const COMMON_DATE_FORMATS = [
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DD',
  'YYYY-MM',
  'YYYY'
];

/**
 * Robust utility to parse any date representation into a Dayjs UTC object.
 * 
 * CRITICAL: This uses strict regex routing to ensure that partial matches 
 * (like '2024' matching '2024-10') never occur.
 * 
 * @param dateVal The date value to parse (string, number, Date, or Dayjs)
 * @param unitHint Optional hint to disambiguate YYYY-XX (e.g., 'week')
 * @returns A Dayjs object (UTC). Check .isValid() to verify success.
 */
export function parseToUtc(dateVal: any, unitHint?: string): dayjs.Dayjs {
  if (dateVal === null || dateVal === undefined || dateVal === '') {
    return dayjs.utc(NaN);
  }

  // 1. Existing object handling
  if (dayjs.isDayjs(dateVal)) return dateVal.utc();
  if (dateVal instanceof Date) return dayjs.utc(dateVal);

  // 2. Numeric handling (e.g., year 2024 vs timestamp)
  if (typeof dateVal === 'number') {
    if (dateVal > 1900 && dateVal < 2100) {
      return dayjs.utc(String(dateVal), 'YYYY', true);
    }
    return dayjs.utc(dateVal);
  }

  const str = String(dateVal).trim();

  // 3. Strict Regex Routing (Prevents partial format matching)
  
  // YYYY-MM-DD HH:mm:ss
  if (/^\d{4}-\d{1,2}-\d{1,2}\s\d{1,2}:\d{1,2}:\d{1,2}$/.test(str)) {
    return dayjs.utc(str, 'YYYY-MM-DD HH:mm:ss', true);
  }

  // YYYY-MM-DD
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(str)) {
    return dayjs.utc(str, 'YYYY-MM-DD', true);
  }

  // YYYY-QN (Quarter format, e.g., "2021-Q1", "2024-Q4")
  const quarterMatch = str.match(/^(\d{4})-Q([1-4])$/i);
  if (quarterMatch) {
    const year = parseInt(quarterMatch[1], 10);
    const quarter = parseInt(quarterMatch[2], 10);
    const month = ((quarter - 1) * 3) + 1; // Q1→1(Jan), Q2→4(Apr), Q3→7(Jul), Q4→10(Oct)
    const monthStr = String(month).padStart(2, '0');
    return dayjs.utc(`${year}-${monthStr}-01`, 'YYYY-MM-DD', true);
  }

  // YYYY-WXX or YYYY-WW (Weeks)
  if (/^\d{4}-W?\d{1,2}$/i.test(str)) {
    const parts = str.toUpperCase().split('-W').join('-').split('-');
    const year = parseInt(parts[0], 10);
    const week = parseInt(parts[1], 10);
    
    // If unit is week or value > 12, it MUST be a week
    if (unitHint === 'week' || week > 12) {
      return dayjs.utc().year(year).isoWeek(week).startOf('isoWeek');
    }
    // If not definitely a week, fall through to YYYY-MM check
  }

  // YYYY-MM
  if (/^\d{4}-\d{1,2}$/.test(str)) {
    return dayjs.utc(str, 'YYYY-MM', true);
  }

  // YYYY (Year only)
  if (/^\d{4}$/.test(str)) {
    return dayjs.utc(str, 'YYYY', true);
  }

  // 4. ISO Fallback (Final resort for 2024-10-01T... or other full strings)
  const d = dayjs.utc(str);
  
  // Final safeguard: if it's just a 4-digit numeric string that native Dayjs 
  // might have parsed as milliseconds, we force it to Year format.
  if (d.isValid() && /^\d{4}$/.test(str)) {
    return dayjs.utc(str, 'YYYY', true);
  }

  return d;
}

/**
 * Shared utility to parse date strings into native Date objects consistently.
 *
 * @param dateVal The date value to parse
 * @param unitHint Optional time unit hint
 * @returns A native Date object or undefined if invalid
 */
export function parseToNativeDate(dateVal: any, unitHint?: string): Date | undefined {
  const d = parseToUtc(dateVal, unitHint);
  if (!d.isValid()) {
    if (dateVal) console.warn(`[date-utils] Failed to parse date: "${dateVal}" (unit: ${unitHint})`);
    return undefined;
  }
  return d.toDate();
}

/**
 * Computes the "previous period" comparison window for [start, end].
 *
 * When the selected range is calendar-aligned (whole year(s) or whole month(s), e.g. the
 * "Last Year" preset's Jan 1 - Dec 31), shifts by whole years/months instead of a raw day
 * count so the result matches the calendar-equivalent dates one year/month prior — this is
 * what the "Previous Year" comparison already does, and keeps them consistent. A raw
 * day-count subtraction (e.g. "365 days before Dec 31, 2024") drifts by a day whenever a
 * leap day falls inside the shifted range, since 2024 has 366 days.
 *
 * For ranges that aren't calendar-aligned (arbitrary custom ranges), falls back to the
 * immediately preceding window of the same day-length, which has no well-defined
 * "calendar-equivalent" alternative.
 */
export function getCalendarEquivalentPreviousPeriod(
  start: dayjs.Dayjs,
  end: dayjs.Dayjs
): { start: dayjs.Dayjs; end: dayjs.Dayjs } {
  const isFullYearRange =
    start.date() === 1 && start.month() === 0 && end.month() === 11 && end.date() === end.daysInMonth();

  if (isFullYearRange) {
    const years = end.year() - start.year() + 1;
    return { start: start.subtract(years, 'year'), end: end.subtract(years, 'year') };
  }

  const isFullMonthRange = start.date() === 1 && end.date() === end.daysInMonth();

  if (isFullMonthRange) {
    const months = end.diff(start, 'month') + 1;
    // Subtracting months from `end` directly can undershoot the target month's last day
    // (e.g. Feb 28 - 1 month = Jan 28, not Jan 31), so derive prevEnd from prevStart instead.
    const prevStart = start.subtract(months, 'month').startOf('month');
    const prevEnd = prevStart.add(months - 1, 'month').endOf('month');
    return { start: prevStart, end: prevEnd };
  }

  const days = end.diff(start, 'day');
  const prevEnd = start.subtract(1, 'day');
  const prevStart = prevEnd.subtract(days, 'day');
  return { start: prevStart, end: prevEnd };
}

export type KpiComparisonLabel = 'vs Previous Period' | 'MoM' | 'Previous Quarter' | 'YoY';

const KPI_MONTH_PRESETS = new Set(['This Month', 'Last Month']);
const KPI_QUARTER_PRESETS = new Set(['This Quarter', 'Last Quarter']);
const KPI_YEAR_PRESETS = new Set(['This Year', 'Last Year', 'Last 2 years', 'Last 3 years', 'Last 5 years']);

/**
 * KPI trend-badge label for the given comparison selection and date range — replaces a
 * hardcoded "MoM" with copy that actually matches the selected filter's *granularity/type*,
 * never just its day-count. A 29-day custom range and a 31-day "This Month" selection must not
 * read the same: only an explicit calendar-month/quarter/year selection earns "MoM" /
 * "Previous Quarter" / "YoY" — every other range (Today, Yesterday, 7/30/90 days, weeks,
 * All Time, arbitrary custom ranges) reads as the generic "vs Previous Period".
 *
 * The label is meant to render AFTER the delta value (e.g. "+20.0% MoM", "-99.16% vs Previous
 * Period") — only the generic "vs Previous Period" case keeps the "vs" prefix; "MoM",
 * "Previous Quarter" and "YoY" read naturally without one.
 *
 * An explicit "Previous Year" comparison-type selection always wins (matches the picker's own
 * YoY toggle) regardless of the selected range. Otherwise the label is derived from the
 * selected preset when there is one, or, for a custom range with no matching preset, from
 * whether the range is itself exactly calendar-aligned (a full month/quarter/year).
 */
export function getComparisonLabel(
  comparisonType: string,
  presetLabel: string | undefined,
  startDate: string,
  endDate: string
): KpiComparisonLabel {
  if (comparisonType === 'previous-year') return 'YoY';
  if (presetLabel) {
    if (KPI_MONTH_PRESETS.has(presetLabel)) return 'MoM';
    if (KPI_QUARTER_PRESETS.has(presetLabel)) return 'Previous Quarter';
    if (KPI_YEAR_PRESETS.has(presetLabel)) return 'YoY';
    return 'vs Previous Period';
  }

  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return 'vs Previous Period';

  const isFullYear =
    start.date() === 1 && start.month() === 0 && end.month() === 11 && end.date() === end.daysInMonth();
  if (isFullYear) return 'YoY';

  const isFullQuarter =
    start.date() === 1 && start.month() % 3 === 0 &&
    end.month() === start.month() + 2 && end.date() === end.daysInMonth();
  if (isFullQuarter) return 'Previous Quarter';

  const isFullMonth =
    start.date() === 1 && start.month() === end.month() && start.year() === end.year() &&
    end.date() === end.daysInMonth();
  if (isFullMonth) return 'MoM';

  return 'vs Previous Period';
}
